import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Globe,
  Edit,
  Eye,
  Save,
  Upload,
  ExternalLink,
  BarChart3,
  CheckCircle,
  XCircle,
  Loader2,
  Rocket,
  Palette
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function WebsiteTab({ customerId }) {
  const [loading, setLoading] = useState(true);
  const [website, setWebsite] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [editing, setEditing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    hero_headline: '',
    hero_subheadline: '',
    about_section: '',
    contact_phone: '',
    contact_email: '',
    business_hours: ''
  });

  useEffect(() => {
    if (customerId) {
      fetchWebsite();
      fetchTemplates();
    }
  }, [customerId]);

  const fetchWebsite = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/websites/me/${customerId}`);
      
      if (response.data.has_website) {
        setWebsite(response.data.website);
        setEditForm({
          hero_headline: response.data.website.pages?.home?.hero_headline || '',
          hero_subheadline: response.data.website.pages?.home?.hero_subheadline || '',
          about_section: response.data.website.pages?.home?.about_section || '',
          contact_phone: response.data.website.pages?.contact?.phone || '',
          contact_email: response.data.website.pages?.contact?.email || '',
          business_hours: response.data.website.pages?.contact?.hours || ''
        });
        
        // Fetch analytics
        fetchAnalytics();
      }
    } catch (error) {
      console.error('Error fetching website:', error);
      toast.error('Failed to load website data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/websites/templates`);
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/websites/me/${customerId}/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const handleGenerateWebsite = async (templateId) => {
    try {
      setGenerating(true);
      const response = await axios.post(`${BACKEND_URL}/api/websites/generate`, {
        customer_id: customerId,
        template_id: templateId
      });
      
      toast.success('Website generated successfully!');
      await fetchWebsite();
    } catch (error) {
      console.error('Error generating website:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate website');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      await axios.put(`${BACKEND_URL}/api/websites/me/${customerId}`, editForm);
      
      toast.success('Website updated successfully!');
      setEditing(false);
      await fetchWebsite();
    } catch (error) {
      console.error('Error updating website:', error);
      toast.error('Failed to update website');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    try {
      setPublishing(true);
      const response = await axios.post(`${BACKEND_URL}/api/websites/me/${customerId}/publish`);
      
      toast.success('Website published successfully!');
      await fetchWebsite();
    } catch (error) {
      console.error('Error publishing website:', error);
      toast.error('Failed to publish website');
    } finally {
      setPublishing(false);
    }
  };

  const [saving, setSaving] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  // No website yet - show template selection
  if (!website) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <Globe className="w-16 h-16 mx-auto mb-4 text-sky-500" />
          <h2 className="text-2xl font-bold text-white mb-2">Create Your Website</h2>
          <p className="text-slate-400 mb-8">
            Choose a template to get started with your AI-powered website
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <motion.div
              key={template.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="group"
            >
              <Card className="bg-slate-800/50 border-slate-700 hover:border-sky-500/50 transition-all cursor-pointer overflow-hidden">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-3xl">{template.icon}</span>
                    <div
                      className="w-12 h-12 rounded-lg"
                      style={{
                        background: `linear-gradient(135deg, ${template.color_scheme.primary}, ${template.color_scheme.accent})`
                      }}
                    />
                  </div>
                  <h3 className="text-xl font-bold text-white">{template.name}</h3>
                  <p className="text-sm text-slate-400">
                    {template.services_default.length} default services
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {template.hero_keywords.slice(0, 3).map((keyword) => (
                      <span
                        key={keyword}
                        className="px-2 py-1 bg-slate-700/50 text-sky-400 text-xs rounded"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                  
                  <Button
                    onClick={() => handleGenerateWebsite(template.id)}
                    disabled={generating}
                    className="w-full bg-sky-500 hover:bg-sky-600"
                    data-testid={`generate-website-${template.id}`}
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Rocket className="w-4 h-4 mr-2" />
                        Generate Website
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  // Website exists - show management UI
  return (
    <div className="space-y-6">
      {/* Header with status and actions */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Globe className="w-6 h-6" />
            Your Website
          </h2>
          <div className="flex items-center gap-2 mt-2">
            {website.published ? (
              <span className="flex items-center gap-1 text-green-400 text-sm">
                <CheckCircle className="w-4 h-4" />
                Published
              </span>
            ) : (
              <span className="flex items-center gap-1 text-yellow-400 text-sm">
                <XCircle className="w-4 h-4" />
                Draft
              </span>
            )}
            <span className="text-slate-400 text-sm">
              • {website.slug}
            </span>
          </div>
        </div>

        <div className="flex gap-2">
          {website.published && (
            <Button
              variant="outline"
              onClick={() => window.open(`https://alaskaaiservices.com/${website.slug}`, '_blank')}
              className="border-slate-700"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              View Live
            </Button>
          )}
          {!editing ? (
            <Button
              onClick={() => setEditing(true)}
              variant="outline"
              className="border-slate-700"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          ) : (
            <>
              <Button
                onClick={() => setEditing(false)}
                variant="outline"
                className="border-slate-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveChanges}
                disabled={saving}
                className="bg-sky-500 hover:bg-sky-600"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </Button>
            </>
          )}
          {!website.published && !editing && (
            <Button
              onClick={handlePublish}
              disabled={publishing}
              className="bg-green-500 hover:bg-green-600"
              data-testid="publish-website-btn"
            >
              {publishing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Rocket className="w-4 h-4 mr-2" />
              )}
              Publish
            </Button>
          )}
        </div>
      </div>

      {/* Analytics Overview */}
      {analytics && (
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total Leads</p>
                  <p className="text-3xl font-bold text-white">{analytics.total_leads}</p>
                </div>
                <BarChart3 className="w-8 h-8 text-sky-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Form Submissions</p>
                  <p className="text-3xl font-bold text-white">{analytics.form_submissions}</p>
                </div>
                <Users className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Visitors</p>
                  <p className="text-3xl font-bold text-white">{analytics.visitors_this_month}</p>
                  <p className="text-xs text-slate-500">This month</p>
                </div>
                <Eye className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Content Editor */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Hero Section */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <h3 className="text-lg font-bold text-white">Hero Section</h3>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm text-slate-400 block mb-2">Headline</label>
              {editing ? (
                <input
                  type="text"
                  value={editForm.hero_headline}
                  onChange={(e) => setEditForm({ ...editForm, hero_headline: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  data-testid="hero-headline-input"
                />
              ) : (
                <p className="text-white font-semibold">{website.pages?.home?.hero_headline}</p>
              )}
            </div>

            <div>
              <label className="text-sm text-slate-400 block mb-2">Subheadline</label>
              {editing ? (
                <textarea
                  value={editForm.hero_subheadline}
                  onChange={(e) => setEditForm({ ...editForm, hero_subheadline: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  data-testid="hero-subheadline-input"
                />
              ) : (
                <p className="text-slate-300">{website.pages?.home?.hero_subheadline}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* About Section */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <h3 className="text-lg font-bold text-white">About Section</h3>
          </CardHeader>
          <CardContent>
            {editing ? (
              <textarea
                value={editForm.about_section}
                onChange={(e) => setEditForm({ ...editForm, about_section: e.target.value })}
                rows={6}
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                data-testid="about-section-input"
              />
            ) : (
              <p className="text-slate-300 whitespace-pre-wrap">{website.pages?.home?.about_section}</p>
            )}
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <h3 className="text-lg font-bold text-white">Contact Information</h3>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm text-slate-400 block mb-2">Phone</label>
              {editing ? (
                <input
                  type="tel"
                  value={editForm.contact_phone}
                  onChange={(e) => setEditForm({ ...editForm, contact_phone: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  data-testid="contact-phone-input"
                />
              ) : (
                <p className="text-white">{website.pages?.contact?.phone}</p>
              )}
            </div>

            <div>
              <label className="text-sm text-slate-400 block mb-2">Email</label>
              {editing ? (
                <input
                  type="email"
                  value={editForm.contact_email}
                  onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  data-testid="contact-email-input"
                />
              ) : (
                <p className="text-white">{website.pages?.contact?.email}</p>
              )}
            </div>

            <div>
              <label className="text-sm text-slate-400 block mb-2">Business Hours</label>
              {editing ? (
                <input
                  type="text"
                  value={editForm.business_hours}
                  onChange={(e) => setEditForm({ ...editForm, business_hours: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  placeholder="Monday-Friday 8AM-5PM"
                  data-testid="business-hours-input"
                />
              ) : (
                <p className="text-white">{website.pages?.contact?.hours}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Color Scheme Preview */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Palette className="w-5 h-5" />
              Color Scheme
            </h3>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div className="flex-1">
                <p className="text-xs text-slate-400 mb-2">Primary</p>
                <div
                  className="h-16 rounded-lg"
                  style={{ backgroundColor: website.color_scheme?.primary }}
                />
                <p className="text-xs text-slate-500 mt-1">{website.color_scheme?.primary}</p>
              </div>
              <div className="flex-1">
                <p className="text-xs text-slate-400 mb-2">Secondary</p>
                <div
                  className="h-16 rounded-lg"
                  style={{ backgroundColor: website.color_scheme?.secondary }}
                />
                <p className="text-xs text-slate-500 mt-1">{website.color_scheme?.secondary}</p>
              </div>
              <div className="flex-1">
                <p className="text-xs text-slate-400 mb-2">Accent</p>
                <div
                  className="h-16 rounded-lg"
                  style={{ backgroundColor: website.color_scheme?.accent }}
                />
                <p className="text-xs text-slate-500 mt-1">{website.color_scheme?.accent}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
